const SESSIONS_PLACEHOLDER = '{QUANTIDADE_DE_SESSOES}';
const SESSIONS_PLACEHOLDER_REGEX = /\{QUANTIDADE_DE_SESSOES\}/g;
const CONTEXTUAL_SESSIONS_REGEX = /(\d+)(\s+)(sessões|sessão|encontros|encontro)/g;

/**
 * Fix Portuguese singular/plural agreement when count is 1.
 */
function applyGrammarFixes(text: string, count: number): string {
    if (count !== 1) return text;
    return text
        .replace(/Foram realizados 1 encontros/g, 'Foi realizado 1 encontro')
        .replace(/Foram realizadas 1 sessões/g, 'Foi realizada 1 sessão')
        .replace(/As 1 sessões ocorreram/g, 'A 1 sessão ocorreu')
        .replace(/1 sessões/g, '1 sessão')
        .replace(/1 encontros/g, '1 encontro');
}

/**
 * Replace the {QUANTIDADE_DE_SESSOES} placeholder with the actual count
 * and apply grammar fixes.
 */
function applySessionCount(text: string, count: number): string {
    const n = Math.trunc(count);
    const replaced = text.replace(SESSIONS_PLACEHOLDER_REGEX, String(n));
    return applyGrammarFixes(replaced, n);
}

/**
 * Used at template-selection time.
 * If count is provided, replaces placeholder with the number + grammar fixes.
 * If not, returns text as-is (placeholder stays visible).
 */
export const formatSessionsText = (text: string, count?: number): string => {
    if (!count) return text;
    return applySessionCount(text, count);
};

/**
 * Used reactively when num_sessions changes and content already exists.
 *
 * 1. If {QUANTIDADE_DE_SESSOES} placeholder is found → replace with count + grammar.
 * 2. Else try contextual regex to find a previously-baked number → swap it.
 * 3. If nothing matched → return null (caller should not update content).
 */
export const replaceSessionsInContent = (content: string, count: number): string | null => {
    if (content.includes(SESSIONS_PLACEHOLDER)) {
        return applySessionCount(content, count);
    }

    const n = Math.trunc(count);

    // Reset lastIndex before test (regex has global flag)
    CONTEXTUAL_SESSIONS_REGEX.lastIndex = 0;
    if (CONTEXTUAL_SESSIONS_REGEX.test(content)) {
        CONTEXTUAL_SESSIONS_REGEX.lastIndex = 0;
        const updated = content.replace(
            CONTEXTUAL_SESSIONS_REGEX,
            (_match, _oldNum, spacing, word) => `${n}${spacing}${word}`,
        );
        return applyGrammarFixes(updated, n);
    }

    // Nothing matched — user fully rewrote the text, don't touch it
    return null;
};
